// Informe de ventas por vendedor (catálogo por vendedor, v1.4). Visible para
// cualquier rol autenticado — el backend decide el alcance: un admin ve una
// fila por cada compañero, un empleado ve solo la suya (ver
// GET /api/reports/vendedores en reportController.js).

import React, { useState, useEffect, useCallback } from 'react';
import { getInformeVendedores } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import { formatCOP } from '../utils/formatters';
import { FaChartLine, FaExclamationTriangle } from 'react-icons/fa';

const InformeVendedoresPage = () => {
  const { user } = useAuth();
  const esAdmin = user?.rol === 'admin_compania' || user?.rol === 'super_admin_sistema';

  const [filas, setFilas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const fetchInforme = useCallback(async (start, end) => {
    try {
      setLoading(true);
      setError(null);
      const data = await getInformeVendedores(start, end);
      setFilas(data);
    } catch (err) {
      setError(err.message || 'No se pudo cargar el informe.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => fetchInforme(startDate || null, endDate || null), 500);
    return () => clearTimeout(handler);
  }, [startDate, endDate, fetchInforme]);

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-8 space-y-6 animate-fadeIn">
      <div>
        <h2 className="text-3xl font-black text-gray-800 tracking-tight flex items-center gap-2">
          <FaChartLine className="text-emerald-500" /> Informe de <span className="text-emerald-500">Vendedores</span>
        </h2>
        <p className="text-gray-500 text-sm">
          {esAdmin
            ? 'Cuánto ha vendido cada quien por su propio enlace del catálogo.'
            : 'Tus propias cifras de ventas por tu enlace del catálogo.'}
        </p>
      </div>

      <div className="bg-white shadow-sm border border-gray-100 rounded-2xl p-4 flex flex-wrap items-end gap-4">
        <div>
          <label className="text-[10px] font-black text-gray-400 uppercase ml-1">Desde</label>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
            className="border border-gray-200 rounded-xl p-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500" />
        </div>
        <div>
          <label className="text-[10px] font-black text-gray-400 uppercase ml-1">Hasta</label>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
            className="border border-gray-200 rounded-xl p-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500" />
        </div>
        {!startDate && !endDate && (
          <p className="text-[11px] text-gray-400 pb-2">Sin fechas, se muestra el mes actual.</p>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-100 rounded-2xl p-4 flex items-center gap-2 text-red-600 text-sm font-medium">
          <FaExclamationTriangle /> {error}
        </div>
      )}

      {loading ? (
        <div className="p-10 text-center text-emerald-600 animate-pulse font-bold">Cargando informe...</div>
      ) : (
        <div className="bg-white shadow-sm border border-gray-100 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50/50 text-gray-400 text-xs font-black uppercase tracking-widest">
                  <th className="px-6 py-4">Vendedor</th>
                  <th className="px-6 py-4 text-right">Total vendido</th>
                  <th className="px-6 py-4 text-center">N.° de ventas</th>
                  <th className="px-6 py-4 text-center">Ventas con saldo pendiente</th>
                  <th className="px-6 py-4 text-center">Pedidos recibidos (todos los estados)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filas.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-10 text-center text-gray-400">Sin datos en este rango de fechas.</td>
                  </tr>
                ) : (
                  filas.map((fila) => (
                    <tr key={fila.userId} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-6 py-4 font-bold text-gray-800">{fila.nombreUsuario}</td>
                      <td className="px-6 py-4 text-right font-black text-emerald-600">{formatCOP(fila.totalVendido)}</td>
                      <td className="px-6 py-4 text-center text-gray-600">{fila.numeroVentas}</td>
                      <td className="px-6 py-4 text-center text-gray-600">{fila.ventasPendientes}</td>
                      <td className="px-6 py-4 text-center text-gray-600">{fila.pedidosRecibidos}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default InformeVendedoresPage;
